import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ApplicantShell from "../components/ApplicantShell";
import config from "../config/config";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { clearStoredAuth } from "../utils/authSession";

const sections = [
  { key: "person", title: "Persoenliche Daten", fields: ["firstname", "middlename", "lastname", "birthday", "birthplace", "birthcountry"] },
  { key: "contact", title: "Kontakt", fields: ["email", "mobile"] },
  { key: "address", title: "Adresse", fields: ["roadname", "housenumber", "aereacode", "town"] },
];

function authHeaders(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function ApplicantEditProfile() {
  const navigate = useNavigate();
  const userid = localStorage.getItem("userid") || "";
  const token = localStorage.getItem("token") || "";
  const [loadErr, setLoadErr] = useState("");
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState("Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.");
  const { register, handleSubmit, reset, watch, formState: { errors, isSubmitting, isDirty } } = useForm({
    defaultValues: {
      userid, email: "", password: "", birthday: "", birthplace: "", birthcountry: "", mobile: "",
      firstname: "", middlename: "", lastname: "", roadname: "", housenumber: "", aereacode: "", town: "",
    },
  });

  const fullname = useMemo(() => [watch("firstname"), watch("middlename"), watch("lastname")].filter(Boolean).join(" ") || "Profil", [watch]);

  const handleAuthError = (error) => {
    if (error?.response?.status !== 401) return false;
    clearStoredAuth();
    const detail = String(error?.response?.data?.detail || error?.message || "").toLowerCase();
    const isTimeout = detail.includes("expired") || detail.includes("inactivity") || detail.includes("session");
    setSessionExpiredMessage(
      isTimeout
        ? "Du wurdest wegen Inaktivitaet automatisch abgemeldet. Bitte melde dich erneut an."
        : "Deine Anmeldung ist nicht mehr gueltig. Bitte melde dich erneut an."
    );
    setSessionExpiredOpen(true);
    return true;
  };

  useEffect(() => {
    if (!userid || !token) {
      navigate("/login");
      return;
    }
    document.title = "Experts4Europe | Profil";
    axios.get(`${config.API_BASE_URL}/api/applicant-data?userid=${userid}`, { headers: authHeaders(token) }).then((response) => {
      const data = response.data || {};
      reset({
        userid: data.userid || userid,
        email: data.email || data["e-mail"] || "",
        birthday: data.birthday || "",
        birthplace: data.birthplace || "",
        birthcountry: data.birthcountry || "",
        mobile: data.mobile || "",
        firstname: data.firstname || "",
        middlename: data.middlename || "",
        lastname: data.lastname || "",
        roadname: data.roadname || "",
        housenumber: data.housenumber || "",
        aereacode: data.aereacode || "",
        town: data.town || "",
      });
    }).catch((error) => {
      if (handleAuthError(error)) return;
      setLoadErr("Fehler beim Laden deines Profils.");
    });
  }, [userid, token, reset, navigate]);

  const onSubmit = async (data) => {
    const payload = {
      userid,
      email: data.email,
      ...(data.password ? { password: data.password } : {}),
      birthday: data.birthday || null,
      birthplace: data.birthplace || null,
      birthcountry: data.birthcountry || null,
      mobile: data.mobile || null,
      firstname: data.firstname || null,
      middlename: data.middlename || null,
      lastname: data.lastname || null,
      roadname: data.roadname || null,
      housenumber: data.housenumber || null,
      aereacode: data.aereacode || null,
      town: data.town || null,
    };
    try {
      await axios.put(`${config.API_BASE_URL}/api/users`, payload, { headers: { ...authHeaders(token), "Content-Type": "application/json" } });
      navigate("/dashboard/applicant");
    } catch (error) {
      if (handleAuthError(error)) return;
      setLoadErr("Fehler beim Speichern deines Profils.");
    }
  };

  return (
    <ApplicantShell
      title="Profil"
      subtitle="Hier pflegst du deine persoenlichen Daten, deinen Kontakt und deine Adresse in derselben Applicant-Struktur."
      actions={<Button className="applicant-cta-button" onClick={handleSubmit(onSubmit)} disabled={isSubmitting}>{isSubmitting ? "Speichert..." : "Profil speichern"}</Button>}
      notifications={[]}
      searchItems={[{ href: "/dashboard/applicant", label: "Dashboard", description: "Zur Mission Control" }, { href: "/dashboard/applicant/bewerbung", label: "Bewerbung", description: "CV und Skills weiter pflegen" }]}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="applicant-workspace-page applicant-workspace-stack">
        {loadErr ? <div className="dashboard-inline-alert dashboard-inline-alert-danger">{loadErr}</div> : null}

        <section className="applicant-workspace-hero applicant-workspace-hero--profile">
          <div className="applicant-workspace-hero__main">
            <span className="applicant-section-kicker">Profil</span>
            <h2>Deine Basis fuer Sichtbarkeit und Kontakt</h2>
            <p>Je klarer dein Profil ist, desto leichter koennen Firmen und das System dich richtig einordnen.</p>
          </div>
          <aside className="applicant-readiness-panel">
            <div className="applicant-readiness-panel__meta applicant-readiness-panel__meta--single">
              <div><span>Anzeigename</span><strong>{fullname}</strong></div>
              <div><span>UserID</span><strong>{userid || "Noch offen"}</strong></div>
            </div>
          </aside>
        </section>

        <section className="applicant-workspace-card">
          <div className="applicant-workspace-card__head">
            <div>
              <span className="applicant-section-kicker">Login</span>
              <h3>Zugang und E-Mail</h3>
            </div>
          </div>
          <div className="applicant-workspace-grid applicant-workspace-grid--double">
            <div className="applicant-workspace-field">
              <Label className="applicant-workspace-label">UserID</Label>
              <Input disabled {...register("userid")} className="applicant-field" />
            </div>
            <div className="applicant-workspace-field">
              <Label className="applicant-workspace-label">E-Mail</Label>
              <Input {...register("email", { required: true })} type="email" className="applicant-field" placeholder="name@example.com" />
              {errors.email ? <small>E-Mail ist erforderlich.</small> : null}
            </div>
          </div>
          <div className="applicant-workspace-field">
            <Label className="applicant-workspace-label">Passwort</Label>
            <Input {...register("password")} type="password" className="applicant-field" placeholder="Nur ausfuellen, wenn du es aendern willst" />
          </div>
        </section>

        {sections.map((section) => (
          <section key={section.key} className="applicant-workspace-card">
            <div className="applicant-workspace-card__head">
              <div>
                <span className="applicant-section-kicker">{section.title}</span>
                <h3>{section.title}</h3>
              </div>
            </div>
            <div className="applicant-workspace-grid applicant-workspace-grid--double">
              {section.fields.includes("firstname") ? <div className="applicant-workspace-field"><Label className="applicant-workspace-label">Vorname</Label><Input {...register("firstname", { required: true })} className="applicant-field" />{errors.firstname ? <small>Pflichtfeld.</small> : null}</div> : null}
              {section.fields.includes("middlename") ? <div className="applicant-workspace-field"><Label className="applicant-workspace-label">Zweitname</Label><Input {...register("middlename")} className="applicant-field" /></div> : null}
              {section.fields.includes("lastname") ? <div className="applicant-workspace-field"><Label className="applicant-workspace-label">Nachname</Label><Input {...register("lastname", { required: true })} className="applicant-field" />{errors.lastname ? <small>Pflichtfeld.</small> : null}</div> : null}
              {section.fields.includes("birthday") ? <div className="applicant-workspace-field"><Label className="applicant-workspace-label">Geburtstag</Label><Input type="date" {...register("birthday")} className="applicant-field" /></div> : null}
              {section.fields.includes("birthplace") ? <div className="applicant-workspace-field"><Label className="applicant-workspace-label">Geburtsort</Label><Input {...register("birthplace")} className="applicant-field" /></div> : null}
              {section.fields.includes("birthcountry") ? <div className="applicant-workspace-field"><Label className="applicant-workspace-label">Geburtsland</Label><Input {...register("birthcountry")} className="applicant-field" /></div> : null}
              {section.fields.includes("mobile") ? <div className="applicant-workspace-field"><Label className="applicant-workspace-label">Mobil</Label><Input {...register("mobile")} className="applicant-field" placeholder="+49 ..." /></div> : null}
              {section.fields.includes("roadname") ? <div className="applicant-workspace-field"><Label className="applicant-workspace-label">Strasse</Label><Input {...register("roadname")} className="applicant-field" /></div> : null}
              {section.fields.includes("housenumber") ? <div className="applicant-workspace-field"><Label className="applicant-workspace-label">Hausnummer</Label><Input {...register("housenumber")} className="applicant-field" /></div> : null}
              {section.fields.includes("aereacode") ? <div className="applicant-workspace-field"><Label className="applicant-workspace-label">PLZ</Label><Input {...register("aereacode")} className="applicant-field" /></div> : null}
              {section.fields.includes("town") ? <div className="applicant-workspace-field"><Label className="applicant-workspace-label">Ort</Label><Input {...register("town")} className="applicant-field" /></div> : null}
            </div>
          </section>
        ))}

        {isDirty ? (
          <div className="applicant-workspace-actions applicant-workspace-actions--sticky">
            <span className="applicant-status-pill">Nicht gespeichert</span>
            <Button className="applicant-cta-button" onClick={handleSubmit(onSubmit)} disabled={isSubmitting}>{isSubmitting ? "Speichert..." : "Jetzt speichern"}</Button>
          </div>
        ) : null}
      </form>

      <Dialog open={sessionExpiredOpen} onOpenChange={() => {}}>
        <DialogContent className="max-w-[460px]">
          <DialogHeader>
            <DialogTitle>Sitzung abgelaufen</DialogTitle>
            <DialogDescription>{sessionExpiredMessage}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" className="applicant-cta-button applicant-cta-button-secondary" onClick={() => navigate("/")}>Startseite</Button>
            <Button className="applicant-cta-button" onClick={() => navigate("/login", { replace: true })}>Erneut anmelden</Button>
          </div>
        </DialogContent>
      </Dialog>
    </ApplicantShell>
  );
}
